// Updated Navigation.jsx - With Life Design Section and Improved Order
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Home, Brain, BookOpen, TrendingUp, Wrench, HelpCircle,
  Shield, Menu, X, User, LogOut, Settings, Sparkles,
  Calendar, Zap, Search, Target, ChevronDown, } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { usePrivacyAuth } from '../../context/PrivacyAuthContext';
import firebaseAnalyticsService from '../../services/analytics/FirebaseAnalyticsService';
import { useGamificationStore } from '../../stores/gamificationStore';

// Daily Challenge Indicator Component - TEMPORARILY DISABLED
const DailyChallengeIndicator = () => {
  // Temporarily return null to fix infinite re-render issue
  return null;
};

// New badge indicator for Life Design series
const NewFeatureBadge = () => (
  <motion.div
    className="w-2 h-2 bg-emerald-400 rounded-full absolute -top-1 -right-1"
    animate={{ scale: [1, 1.2, 1] }}
    transition={{ duration: 2, repeat: Infinity }}
  />
);

const Navigation = ({ isGuestMode = false }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const { currentView, setCurrentView, assessmentResults, completedModules, completedTools, userProgress } = useApp();
  const { user, logout } = usePrivacyAuth();

  // Use Zustand gamification store instead of context
  const { currentLevel, totalXP, currentLevelData } = useGamificationStore();
  
  // Check if user has relevant data for dashboard
  const [hasRelevantData, setHasRelevantData] = useState(false);
  
  useEffect(() => {
    const checkRelevantData = () => {
      // Check AppContext first for assessment results
      const hasAssessmentFromContext = assessmentResults && Object.keys(assessmentResults).length > 0;
      
      // Check AppContext for completed modules/tools - THIS IS THE KEY FIX!
      const hasCompletedModules = completedModules && completedModules.length > 0;
      const hasCompletedTools = completedTools && completedTools.length > 0;
      const hasUserProgress = userProgress && Object.keys(userProgress).length > 0;
      
      // Check localStorage for assessment with correct key pattern
      let hasAssessmentFromStorage = false;
      try {
        const guestAssessment = localStorage.getItem('vibe_assessment_results_guest');
        if (guestAssessment) {
          const parsed = JSON.parse(guestAssessment);
          hasAssessmentFromStorage = parsed && (parsed.results || parsed.overallResults);
        }
        
        if (user?.userId) {
          const userAssessment = localStorage.getItem(`vibe_assessment_results_${user.userId}`);
          if (userAssessment) {
            const parsed = JSON.parse(userAssessment);
            hasAssessmentFromStorage = hasAssessmentFromStorage || (parsed && (parsed.results || parsed.overallResults));
          }
        }
      } catch (error) {
        console.warn('Error checking assessment in navigation:', error);
      }
      
      // Check localStorage for progress data with multiple potential keys
      let hasModuleProgress = false;
      let hasGamificationData = false;
      
      try {
        // Check standard and user-specific progress keys
        const progressKeys = ['vibeUserProgress'];
        if (user?.userId) {
          progressKeys.push(`vibe_progress_data_${user.userId}`);
        }
        
        for (const key of progressKeys) {
          const moduleProgress = localStorage.getItem(key);
          if (moduleProgress) {
            const parsed = JSON.parse(moduleProgress);
            if (parsed && Object.keys(parsed).length > 0) {
              hasModuleProgress = true;
              break;
            }
          }
        }
        
        // Check gamification data
        const gamificationKeys = [
          'vibeGamificationData',
          user?.userId ? `vibeGamificationData_${user.userId}` : 'vibeGamificationData_guest'
        ];
        
        for (const key of gamificationKeys) {
          const gamificationData = localStorage.getItem(key);
          if (gamificationData) {
            const parsed = JSON.parse(gamificationData);
            if (parsed && (parsed.totalXP > 0 || (parsed.completedModules && parsed.completedModules.length > 0))) {
              hasGamificationData = true;
              break;
            }
          }
        }
      } catch (error) {
        console.warn('Error checking progress data in navigation:', error);
      }
      
      const hasAssessment = hasAssessmentFromContext || hasAssessmentFromStorage;
      
      // Return true if ANY relevant data exists
      const result = hasAssessment || hasModuleProgress || hasGamificationData || 
             hasCompletedModules || hasCompletedTools || hasUserProgress;
      
      // Navigation data check logging disabled to prevent console spam
      // console.log('🧭 Navigation data check:', { result: result ? 'ALLOW DASHBOARD' : 'REDIRECT TO ASSESSMENT' });
      
      setHasRelevantData(result);
    };
    
    checkRelevantData();
    // DISABLED: Aggressive refresh causing page refresh issues
    // Re-check when view changes (in case user completes assessment)
    // const interval = setInterval(checkRelevantData, 5000);
    // return () => clearInterval(interval);
  }, [currentView, assessmentResults, completedModules, completedTools, userProgress, user?.userId]);

  // Navigation order: Assessment, Dive In, Your Toolkit, Dashboard, Resources, Coach, Login
  const navItems = [
    {
      id: 'vibe-assessment-intro',
      label: 'Assessment',
      icon: Brain,
      highlight: !user, // Highlight for guests
      hasDropdown: true,
      dropdownItems: [
        { id: 'vibe-assessment-intro', label: 'Take Assessment', icon: Brain },
        { id: 'vibe-assessment-results', label: 'View My Results', icon: TrendingUp },
      ],
    },
    {
      id: 'modules',
      label: 'Dive In',
      icon: BookOpen,
    },
    {
      id: 'tools',
      label: 'Your Toolkit',
      icon: Wrench,
    },
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: Calendar,
      indicator: () => null, // Temporarily disabled
    },
    {
      id: 'resources',
      label: 'Resources',
      icon: HelpCircle,
    },
    {
      id: 'coaching',
      label: 'Coach',
      icon: Sparkles,
      isNew: true,
    },
    // Auth section handled separately in user section
  ];

  const handleNavClick = (viewId) => {
    // Track navigation event
    firebaseAnalyticsService.trackScreenView(viewId, 'navigation');
    
    // Dashboard is now always accessible - it shows onboarding if no data
    // No need to block dashboard access since it gracefully handles empty state
    
    // For guests trying to access locked content - only data-privacy requires auth
    // Dashboard is now guest-friendly with limited features
    if (!user && ['data-privacy'].includes(viewId)) {
      // Redirect to pure HTML signup page
      window.location.href = '/signup.html';
      return;
    }
    
    // Handle Life Design routing
    if (viewId === 'life-design-landing' && !user) {
      setCurrentView('life-design-landing');
      setIsMobileMenuOpen(false);
      setOpenDropdown(null);
      return;
    }
    
    // Handle logout
    if (viewId === 'logout') {
      handleLogout();
      return;
    }
    
    // Handle login - redirect to HTML login page
    if (viewId === 'login') {
      window.location.href = '/login.html';
      return;
    }
    
    setCurrentView(viewId);
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  };

  const handleDropdownToggle = (itemId) => {
    setOpenDropdown(openDropdown === itemId ? null : itemId);
  };

  const handleDropdownItemClick = (viewId) => {
    // Handle logout
    if (viewId === 'logout') {
      handleLogout();
      return;
    }
    
    // Track dropdown navigation event
    firebaseAnalyticsService.trackScreenView(viewId, 'dropdown');
    
    setCurrentView(viewId);
    setOpenDropdown(null);
    setIsMobileMenuOpen(false);
  };

  const handleLogout = () => {
    logout(true);
    setShowUserMenu(false);
    // Redirect to home page after logout to avoid signup popup
    setCurrentView('home');
  };


  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-sm border-b border-slate-700">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-28">
          
          {/* ===== LOGO SECTION ===== */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => handleNavClick('home')}
          >
            {/* MindEase Vibe logo */}
            <div className="flex items-center justify-center">
              <img
                src="/mindease-vibe-logo-350px.png"
                alt="MindEase Vibe"
                className="w-24 h-24"
              />
            </div>

            {/* Brand Text */}
            <div className="flex flex-col">
              <span className="font-bold text-xl text-white leading-none">MindEase Vibe</span>
              <span className="text-xs text-purple-300 font-light leading-none -mt-0.5">
                A Dive Deep Dialogues app
              </span>
            </div>
          </motion.div>

          {/* ===== DESKTOP NAVIGATION - NEW ORDER ===== */}
          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <div key={item.id} className="relative">
                {item.hasDropdown ? (
                  <>
                    <motion.button
                      onClick={() => handleDropdownToggle(item.id)}
                      className={`px-3 py-2 rounded-lg font-medium transition-all flex items-center gap-2 text-sm relative ${
                        item.dropdownItems?.some(dropItem => currentView === dropItem.id)
                          ? 'bg-purple-600 text-white shadow-lg'
                          : 'text-slate-300 hover:text-white hover:bg-slate-800/70'
                      }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <item.icon className="w-4 h-4" />
                      <span>{item.label}</span>
                      <ChevronDown className={`w-3 h-3 transition-transform ${
                        openDropdown === item.id ? 'rotate-180' : ''
                      }`} />
                      {item.dropdownItems?.some(dropItem => dropItem.isNew) && <NewFeatureBadge />}
                    </motion.button>
                    
                    {openDropdown === item.id && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute top-full left-0 mt-2 w-48 bg-slate-800 rounded-lg shadow-xl border border-slate-700 z-50"
                      >
                        {item.dropdownItems.map((dropItem) => (
                          <motion.button
                            key={dropItem.id}
                            onClick={() => handleDropdownItemClick(dropItem.id)}
                            className={`w-full px-4 py-3 text-left text-sm transition-all flex items-center gap-3 first:rounded-t-lg last:rounded-b-lg ${
                              currentView === dropItem.id
                                ? 'bg-purple-600 text-white'
                                : dropItem.isNew
                                ? 'text-emerald-300 hover:text-white hover:bg-emerald-800/30'
                                : 'text-slate-300 hover:text-white hover:bg-slate-700'
                            }`}
                            whileHover={{ x: 2 }}
                          >
                            <dropItem.icon className="w-4 h-4" />
                            <span>{dropItem.label}</span>
                            {dropItem.isNew && (
                              <span className="ml-auto px-2 py-0.5 bg-emerald-500/20 text-emerald-300 text-xs rounded-full font-medium">
                                New
                              </span>
                            )}
                          </motion.button>
                        ))}
                      </motion.div>
                    )}
                  </>
                ) : (
                  <motion.button
                    onClick={() => handleNavClick(item.id)}
                    className={`px-3 py-2 rounded-lg font-medium transition-all flex items-center gap-2 text-sm relative ${
                      currentView === item.id
                        ? 'bg-purple-600 text-white shadow-lg'
                        : item.isNew
                        ? 'text-emerald-300 hover:text-white hover:bg-emerald-800/30 ring-1 ring-emerald-500/30'
                        : 'text-slate-300 hover:text-white hover:bg-slate-800/70'
                    } ${item.highlight ? 'ring-1 ring-purple-500/50' : ''}`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <item.icon className="w-4 h-4" />
                    <span>{item.label}</span>
                    {item.indicator && <item.indicator />}
                    {item.isNew && <NewFeatureBadge />}
                    {item.highlight && (
                      <motion.div
                        className="absolute -top-1 -right-1 w-2 h-2 bg-purple-400 rounded-full"
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      />
                    )}
                  </motion.button>
                )}
              </div>
            ))}
          </div>

          {/* ===== USER SECTION ===== */}
          <div className="flex items-center gap-3">
            {user ? (
              <div className="hidden md:flex items-center gap-3">
                {/* User Level Badge */}
                {currentLevel && (
                  <div className="flex items-center gap-2 bg-slate-800/50 rounded-lg px-3 py-1.5">
                    <div className="text-xs text-slate-400">Level</div>
                    <div className="text-sm font-bold text-white">{currentLevel}</div>
                    <div className="text-xs">{currentLevelData?.emoji || '🌱'}</div>
                  </div>
                )}
                
                {/* User Menu */}
                <div className="relative">
                  <motion.button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2 bg-slate-800/50 hover:bg-slate-800 rounded-lg px-3 py-1.5 transition-colors"
                    whileHover={{ scale: 1.02 }}
                  >
                    <User className="w-4 h-4 text-slate-300" />
                    <span className="text-sm text-white font-medium">{user.username || user.email || 'Profile'}</span>
                  </motion.button>
                  
                  {showUserMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="absolute top-full right-0 mt-2 w-48 bg-slate-800 rounded-lg shadow-xl border border-slate-700 py-1"
                    >
                      <button
                        onClick={() => handleNavClick('data-privacy')}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700"
                      >
                        <Shield className="w-4 h-4" />
                        Privacy & Data
                      </button>
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign Out
                      </button>
                    </motion.div>
                  )}
                </div>
              </div>
            ) : (
              <div className="hidden md:flex items-center gap-3">
                {/* Login Button */}
                <motion.button
                  onClick={() => {
                    // Preserve current view for return after login
                    const returnView = currentView !== 'home' ? currentView : '';
                    const loginUrl = returnView ? `/login.html?redirect=${returnView}` : '/login.html';
                    window.location.href = loginUrl;
                  }}
                  className="flex items-center gap-2 text-slate-300 hover:text-white hover:bg-slate-800/50 px-4 py-2 rounded-lg font-medium transition-all text-sm"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <User className="w-4 h-4" />
                  <span>Login</span>
                </motion.button>
                {/* Signup Button */}
                <motion.button
                  onClick={() => {
                    const returnView = currentView !== 'home' ? currentView : '';
                    const signupUrl = returnView ? `/signup.html?redirect=${returnView}` : '/signup.html';
                    window.location.href = signupUrl;
                  }}
                  className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium transition-all text-sm shadow-lg"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Sparkles className="w-4 h-4" />
                  <span>Sign Up Free</span>
                </motion.button>
              </div>
            )}

            {/* Mobile Menu Button */}
            <motion.button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-slate-300 hover:text-white rounded-lg hover:bg-slate-800/50 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" aria-hidden="true" /> : <Menu className="w-5 h-5" aria-hidden="true" />}
            </motion.button>
          </div>
        </div>

        {/* ===== MOBILE NAVIGATION - UPDATED ORDER ===== */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-t border-slate-700 py-4"
          >
            <div className="flex flex-col gap-2">
              {navItems.map((item) => (
                <div key={item.id} className="relative">
                  {item.hasDropdown ? (
                    <>
                      <motion.button
                        onClick={() => handleDropdownToggle(item.id)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all w-full text-left ${
                          item.dropdownItems?.some(dropItem => currentView === dropItem.id)
                            ? 'bg-purple-600 text-white'
                            : 'text-slate-300 hover:text-white hover:bg-slate-800/50'
                        }`}
                        whileHover={{ x: 2 }}
                      >
                        <item.icon className="w-5 h-5" aria-hidden="true" />
                        <span>{item.label}</span>
                        <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${
                          openDropdown === item.id ? 'rotate-180' : ''
                        }`} aria-hidden="true" />
                        {item.dropdownItems?.some(dropItem => dropItem.isNew) && (
                          <Sparkles className="w-4 h-4 text-emerald-400" aria-hidden="true" />
                        )}
                      </motion.button>
                      
                      {openDropdown === item.id && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="ml-6 mt-2 space-y-1"
                        >
                          {item.dropdownItems.map((dropItem) => (
                            <motion.button
                              key={dropItem.id}
                              onClick={() => handleDropdownItemClick(dropItem.id)}
                              className={`flex items-center gap-3 px-4 py-2 rounded-lg font-medium transition-all w-full text-left ${
                                currentView === dropItem.id
                                  ? 'bg-purple-600 text-white'
                                  : dropItem.isNew
                                  ? 'text-emerald-300 hover:text-white hover:bg-emerald-800/30'
                                  : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
                              }`}
                              whileHover={{ x: 2 }}
                            >
                              <dropItem.icon className="w-4 h-4" aria-hidden="true" />
                              <span>{dropItem.label}</span>
                              {dropItem.isNew && (
                                <div className="flex items-center gap-1 ml-auto">
                                  <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-300 text-xs rounded-full font-medium">
                                    New
                                  </span>
                                </div>
                              )}
                            </motion.button>
                          ))}
                        </motion.div>
                      )}
                    </>
                  ) : (
                    <motion.button
                      onClick={() => handleNavClick(item.id)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all w-full text-left ${
                        currentView === item.id
                          ? 'bg-purple-600 text-white'
                          : item.isNew
                          ? 'text-emerald-300 hover:text-white hover:bg-emerald-800/30'
                          : 'text-slate-300 hover:text-white hover:bg-slate-800/50'
                      }`}
                      whileHover={{ x: 2 }}
                    >
                      <item.icon className="w-5 h-5" aria-hidden="true" />
                      <span>{item.label}</span>
                      {item.indicator && <item.indicator />}
                      {item.isNew && (
                        <div className="flex items-center gap-1 ml-auto">
                          <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-300 text-xs rounded-full font-medium">
                            New
                          </span>
                          <Sparkles className="w-4 h-4 text-emerald-400" />
                        </div>
                      )}
                      {item.highlight && (
                        <Sparkles className="w-4 h-4 text-purple-400 ml-auto" />
                      )}
                    </motion.button>
                  )}
                </div>
              ))}
              
              {/* Mobile User Section */}
              {user ? (
                <div className="border-t border-slate-700 pt-4 mt-2 space-y-2">
                  {currentLevel && (
                    <div className="flex items-center justify-between px-4 py-2 bg-slate-800/30 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-400">Level {currentLevel}</span>
                        <span className="text-sm">{currentLevelData?.emoji || '🌱'}</span>
                      </div>
                      <span className="text-xs text-slate-500">{totalXP || 0} XP</span>
                    </div>
                  )}
                  
                  <button
                    onClick={() => handleNavClick('data-privacy')}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800/50 w-full text-left"
                  >
                    <Shield className="w-5 h-5" />
                    <span>Privacy & Data</span>
                  </button>
                  
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-300 hover:text-white hover:bg-red-600/20 w-full text-left"
                  >
                    <LogOut className="w-5 h-5" />
                    <span>Sign Out</span>
                  </button>
                </div>
              ) : (
                <div className="border-t border-slate-700 pt-4 mt-2 space-y-2">
                  <motion.button
                    onClick={() => {
                      const returnView = currentView !== 'home' ? currentView : '';
                      const loginUrl = returnView ? `/login.html?redirect=${returnView}` : '/login.html';
                      window.location.href = loginUrl;
                    }}
                    className="flex items-center gap-3 text-slate-300 hover:text-white hover:bg-slate-800/50 px-4 py-3 rounded-lg font-medium transition-all w-full"
                    whileHover={{ x: 2 }}
                  >
                    <User className="w-5 h-5" />
                    <span>Login</span>
                  </motion.button>

                  <motion.button
                    onClick={() => {
                      const returnView = currentView !== 'home' ? currentView : '';
                      const signupUrl = returnView ? `/signup.html?redirect=${returnView}` : '/signup.html';
                      window.location.href = signupUrl;
                    }}
                    className="flex items-center gap-3 bg-purple-600 hover:bg-purple-700 text-white px-4 py-3 rounded-lg font-medium transition-all w-full shadow-lg"
                    whileHover={{ x: 2 }}
                  >
                    <Sparkles className="w-5 h-5" />
                    <span>Sign Up Free</span>
                  </motion.button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;